import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchArizaListe,
  fetchArizaOzet,
  fetchArizaMusteri,
  fetchArizaMusteriServisKayitlari,
  fetchArizaMusteriZaman,
  fetchArizaUrun,
  fetchArizaUrunArizalar,
  fetchArizaUrunServisKayitlari,
  fetchArizaUrunZaman,
  fetchArizaUrunleri,
  fetchArizaZaman,
} from '../services/istatistikService';
import { formatApiDate } from '../utils/security';
import TableListFooter from '../components/TableListFooter';
import DonemSecici from '../components/istatistik/DonemSecici';
import ArizaHalka from '../components/istatistik/ArizaHalka';
import ArizaZamanCubuk from '../components/istatistik/ArizaZamanCubuk';
import './IstatistikArizaPage.css';

const PAGE_SIZE = 25;
const MUSTERI_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;
const TABS = [
  { id: 'urun', label: 'Ürün' },
  { id: 'musteri', label: 'Müşteri' },
  { id: 'ariza', label: 'Arıza' },
];

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const useDebouncedSearch = (value) => {
  const [debounced, setDebounced] = useState(value.trim());
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [value]);
  return debounced.length >= 3 ? debounced : '';
};

const EmptyState = ({ icon, text }) => (
  <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
    <i className={`fas ${icon}`} style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
    <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>{text}</div>
  </div>
);

const SearchBox = ({ value, onChange, placeholder, loading, label }) => (
  <div className="admin-finance-toolbar">
    <div className="admin-finance-search-row">
      <div className="search-box admin-finance-search-box">
        <i className="fas fa-search" aria-hidden="true"></i>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={80}
          aria-label={label}
        />
        {loading && value.trim().length >= 3 && (
          <i className="fas fa-spinner fa-spin admin-finance-search-spinner" aria-hidden="true"></i>
        )}
      </div>
    </div>
  </div>
);

const IstatistikArizaPage = () => {
  const [activeTab, setActiveTab] = useState('urun');
  const [donem, setDonem] = useState('1yil');
  const [ozet, setOzet] = useState(null);
  const [ozetLoading, setOzetLoading] = useState(false);

  const [urunSearch, setUrunSearch] = useState('');
  const [musteriSearch, setMusteriSearch] = useState('');
  const [arizaSearch, setArizaSearch] = useState('');
  const urunArama = useDebouncedSearch(urunSearch);
  const musteriArama = useDebouncedSearch(musteriSearch);
  const arizaArama = useDebouncedSearch(arizaSearch);

  const [urunler, setUrunler] = useState([]);
  const [musteriler, setMusteriler] = useState([]);
  const [arizalar, setArizalar] = useState([]);
  const [urunPage, setUrunPage] = useState(1);
  const [musteriPage, setMusteriPage] = useState(1);
  const [arizaPage, setArizaPage] = useState(1);
  const [urunMeta, setUrunMeta] = useState({ total: 0, totalPages: 1 });
  const [musteriMeta, setMusteriMeta] = useState({ total: 0, totalPages: 1 });
  const [arizaMeta, setArizaMeta] = useState({ total: 0, totalPages: 1 });
  const [urunLoading, setUrunLoading] = useState(false);
  const [musteriLoading, setMusteriLoading] = useState(false);
  const [arizaLoadingList, setArizaLoadingList] = useState(false);
  const [urunError, setUrunError] = useState('');
  const [musteriError, setMusteriError] = useState('');
  const [arizaError, setArizaError] = useState('');

  const [expandedUrun, setExpandedUrun] = useState(null);
  const [expandedAriza, setExpandedAriza] = useState(null);
  const [arizaUrunCache, setArizaUrunCache] = useState({});
  const [arizaUrunLoading, setArizaUrunLoading] = useState(null);
  const [zamanCache, setZamanCache] = useState({});
  const [zamanLoading, setZamanLoading] = useState(null);
  const [arizaCache, setArizaCache] = useState({});
  const [arizaLoading, setArizaLoading] = useState(null);

  const [expandedMusteri, setExpandedMusteri] = useState(null);
  const [kayitCache, setKayitCache] = useState({});
  const [kayitLoading, setKayitLoading] = useState(null);
  const [kayitPage, setKayitPage] = useState({});

  const [modal, setModal] = useState(null);
  const [modalPage, setModalPage] = useState(1);
  const [modalRows, setModalRows] = useState([]);
  const [modalMeta, setModalMeta] = useState({ total: 0, totalPages: 1 });
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => setUrunPage(1), [urunArama]);
  useEffect(() => setMusteriPage(1), [musteriArama]);
  useEffect(() => setArizaPage(1), [arizaArama]);

  useEffect(() => {
    setUrunPage(1);
    setMusteriPage(1);
    setArizaPage(1);
    setExpandedUrun(null);
    setExpandedMusteri(null);
    setExpandedAriza(null);
    setArizaCache({});
    setKayitCache({});
    setArizaUrunCache({});
    setZamanCache({});
    setModal(null);
  }, [donem]);

  const loadUrunler = useCallback(async () => {
    setUrunLoading(true);
    setUrunError('');
    try {
      const response = await fetchArizaUrun({ sayfa: urunPage, limit: PAGE_SIZE, arama: urunArama || undefined, donem });
      if (response?.basarili && response?.veri) {
        setUrunler(response.veri.urunler || []);
        const p = response.veri.sayfalama || {};
        setUrunMeta({ total: p.toplamKayit ?? 0, totalPages: Math.max(1, p.toplamSayfa || 1) });
      } else {
        setUrunler([]);
        setUrunMeta({ total: 0, totalPages: 1 });
      }
    } catch (err) {
      setUrunError(getErrorMessage(err, 'Liste yüklenemedi'));
      setUrunler([]);
    } finally {
      setUrunLoading(false);
    }
  }, [urunPage, urunArama, donem]);

  const loadMusteriler = useCallback(async () => {
    setMusteriLoading(true);
    setMusteriError('');
    try {
      const response = await fetchArizaMusteri({
        sayfa: musteriPage,
        limit: PAGE_SIZE,
        arama: musteriArama || undefined,
        donem,
      });
      if (response?.basarili && response?.veri) {
        setMusteriler(response.veri.musteriler || []);
        const p = response.veri.sayfalama || {};
        setMusteriMeta({ total: p.toplamKayit ?? 0, totalPages: Math.max(1, p.toplamSayfa || 1) });
      } else {
        setMusteriler([]);
        setMusteriMeta({ total: 0, totalPages: 1 });
      }
    } catch (err) {
      setMusteriError(getErrorMessage(err, 'Liste yüklenemedi'));
      setMusteriler([]);
    } finally {
      setMusteriLoading(false);
    }
  }, [musteriPage, musteriArama, donem]);

  useEffect(() => {
    if (activeTab === 'urun') loadUrunler();
  }, [activeTab, loadUrunler]);

  useEffect(() => {
    if (activeTab === 'musteri') loadMusteriler();
  }, [activeTab, loadMusteriler]);

  const loadArizaListe = useCallback(async () => {
    setArizaLoadingList(true);
    setArizaError('');
    try {
      const response = await fetchArizaListe({ sayfa: arizaPage, limit: PAGE_SIZE, arama: arizaArama || undefined, donem });
      if (response?.basarili && response?.veri) {
        setArizalar(response.veri.arizalar || []);
        const p = response.veri.sayfalama || {};
        setArizaMeta({ total: p.toplamKayit ?? 0, totalPages: Math.max(1, p.toplamSayfa || 1) });
      } else {
        setArizalar([]);
        setArizaMeta({ total: 0, totalPages: 1 });
      }
    } catch (err) {
      setArizaError(getErrorMessage(err, 'Liste yüklenemedi'));
      setArizalar([]);
    } finally {
      setArizaLoadingList(false);
    }
  }, [arizaPage, arizaArama, donem]);

  useEffect(() => {
    if (activeTab === 'ariza') loadArizaListe();
  }, [activeTab, loadArizaListe]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setOzetLoading(true);
      try {
        const response = await fetchArizaOzet({ sekme: activeTab, donem });
        if (!cancelled) setOzet(response?.basarili ? response.veri : null);
      } catch {
        if (!cancelled) setOzet(null);
      } finally {
        if (!cancelled) setOzetLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [activeTab, donem]);

  const loadZaman = useCallback(async (key, loader) => {
    if (zamanCache[key]) return;
    setZamanLoading(key);
    try {
      const response = await loader();
      const seri = response?.basarili ? response.veri : { noktalar: [] };
      setZamanCache((prev) => ({ ...prev, [key]: seri }));
    } catch {
      setZamanCache((prev) => ({ ...prev, [key]: { noktalar: [] } }));
    } finally {
      setZamanLoading(null);
    }
  }, [zamanCache]);

  const loadArizalar = useCallback(async (urunId) => {
    if (arizaCache[urunId]) return;
    setArizaLoading(urunId);
    try {
      const response = await fetchArizaUrunArizalar(urunId, donem);
      const liste = response?.basarili ? (response.veri?.arizalar || []) : [];
      setArizaCache((prev) => ({ ...prev, [urunId]: liste }));
    } catch {
      setArizaCache((prev) => ({ ...prev, [urunId]: [] }));
    } finally {
      setArizaLoading(null);
    }
  }, [arizaCache, donem]);

  const loadKayitlar = useCallback(async (musteriId, sayfa) => {
    setKayitLoading(musteriId);
    try {
      const response = await fetchArizaMusteriServisKayitlari(musteriId, { sayfa, limit: MUSTERI_PAGE_SIZE, donem });
      const veri = response?.veri || {};
      const p = veri.sayfalama || {};
      setKayitCache((prev) => ({
        ...prev,
        [musteriId]: {
          kayitlar: veri.servisKayitlari || [],
          total: p.toplamKayit ?? 0,
          totalPages: Math.max(1, p.toplamSayfa || 1),
        },
      }));
    } catch {
      setKayitCache((prev) => ({
        ...prev,
        [musteriId]: { kayitlar: [], total: 0, totalPages: 1 },
      }));
    } finally {
      setKayitLoading(null);
    }
  }, [donem]);

  const toggleUrun = (urun) => {
    if (!urun.toplamAriza) return;
    setExpandedUrun((prev) => (prev === urun.urunId ? null : urun.urunId));
    if (expandedUrun !== urun.urunId) {
      loadArizalar(urun.urunId);
      loadZaman(`urun:${urun.urunId}`, () => fetchArizaUrunZaman(urun.urunId, donem));
    }
  };

  const toggleMusteri = (musteriId) => {
    const next = expandedMusteri === musteriId ? null : musteriId;
    setExpandedMusteri(next);
    if (next) {
      const sayfa = kayitPage[musteriId] || 1;
      loadKayitlar(musteriId, sayfa);
      loadZaman(`musteri:${musteriId}`, () => fetchArizaMusteriZaman(musteriId, donem));
    }
  };

  const changeKayitPage = (musteriId, sayfa) => {
    setKayitPage((prev) => ({ ...prev, [musteriId]: sayfa }));
    loadKayitlar(musteriId, sayfa);
  };

  const openModal = (urun, ariza) => {
    setModal({
      urunId: urun.urunId,
      urunAdi: urun.urunAdi,
      arizaId: ariza.arizaId,
      arizaAdi: ariza.arizaAdi,
    });
    setModalPage(1);
  };

  useEffect(() => {
    if (!modal) return undefined;
    let cancelled = false;
    const run = async () => {
      setModalLoading(true);
      setModalError('');
      try {
        const response = await fetchArizaUrunServisKayitlari(modal.urunId, modal.arizaId, {
          sayfa: modalPage,
          limit: PAGE_SIZE,
          donem,
        });
        if (cancelled) return;
        const veri = response?.veri || {};
        const p = veri.sayfalama || {};
        setModalRows(veri.servisKayitlari || []);
        setModalMeta({ total: p.toplamKayit ?? 0, totalPages: Math.max(1, p.toplamSayfa || 1) });
      } catch (err) {
        if (cancelled) return;
        setModalError(getErrorMessage(err, 'Servis kayıtları yüklenemedi'));
        setModalRows([]);
      } finally {
        if (!cancelled) setModalLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [modal, modalPage, donem]);

  useEffect(() => {
    if (!modal) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [modal]);

  const renderUrun = () => {
    if (urunLoading && urunler.length === 0) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Liste yükleniyor</div>
        </div>
      );
    }
    if (urunError) return <div className="error-state">{urunError}</div>;
    if (urunler.length === 0) return <EmptyState icon="fa-box" text="Ürün bulunamadı" />;

    return (
      <>
        <div className="table-container">
          <table className="admins-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Ürün</th>
                <th>Kod</th>
                <th>Arıza</th>
              </tr>
            </thead>
            <tbody>
              {urunler.map((u) => {
                const disabled = !u.toplamAriza;
                const isExpanded = expandedUrun === u.urunId;
                const arizalar = arizaCache[u.urunId];
                const loadingAriza = arizaLoading === u.urunId;
                return (
                  <React.Fragment key={u.urunId}>
                    <tr
                      className={`${isExpanded ? 'expanded-row' : ''} ${disabled ? 'ia-row--disabled' : ''}`}
                      onClick={() => toggleUrun(u)}
                      style={{ cursor: disabled ? 'default' : 'pointer' }}
                    >
                      <td>
                        {!disabled && (
                          <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: 'var(--text-gray)' }}></i>
                        )}
                      </td>
                      <td style={{ fontWeight: 500 }}>{u.urunAdi || '—'}</td>
                      <td>{u.urunKodu || '—'}</td>
                      <td>{u.toplamAriza}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={4} className="ia-expand-cell">
                          <div className="ia-panel">
                            <ArizaZamanCubuk
                              seri={zamanCache[`urun:${u.urunId}`]}
                              loading={zamanLoading === `urun:${u.urunId}`}
                            />
                            {loadingAriza ? (
                              <div className="ia-empty-inline"><i className="fas fa-spinner fa-spin"></i> Yükleniyor</div>
                            ) : Array.isArray(arizalar) && arizalar.length > 0 ? (
                              <div className="ia-fault-list">
                                {arizalar.map((a) => (
                                  <button
                                    key={a.arizaId}
                                    type="button"
                                    className="ia-fault-btn"
                                    onClick={() => openModal(u, a)}
                                  >
                                    <span>{a.arizaAdi}</span>
                                    <span className="ia-count">{a.adet}</span>
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="ia-empty-inline">Arıza bulunamadı</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <TableListFooter
          totalRecords={urunMeta.total}
          currentPage={urunPage}
          totalPages={urunMeta.totalPages || 1}
          onPageChange={setUrunPage}
          loading={urunLoading}
          embedded
        />
      </>
    );
  };

  const renderMusteri = () => {
    if (musteriLoading && musteriler.length === 0) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Liste yükleniyor</div>
        </div>
      );
    }
    if (musteriError) return <div className="error-state">{musteriError}</div>;
    if (musteriler.length === 0) return <EmptyState icon="fa-users" text="Müşteri bulunamadı" />;

    return (
      <>
        <div className="table-container">
          <table className="admins-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Müşteri</th>
                <th>Servis Kaydı</th>
              </tr>
            </thead>
            <tbody>
              {musteriler.map((m) => {
                const isExpanded = expandedMusteri === m.musteriId;
                const paket = kayitCache[m.musteriId];
                const loadingKayit = kayitLoading === m.musteriId;
                const sayfa = kayitPage[m.musteriId] || 1;
                return (
                  <React.Fragment key={m.musteriId}>
                    <tr
                      className={isExpanded ? 'expanded-row' : ''}
                      onClick={() => toggleMusteri(m.musteriId)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: 'var(--text-gray)' }}></i>
                      </td>
                      <td style={{ fontWeight: 500 }}>{m.musteriAdi || '—'}</td>
                      <td>{m.servisKaydiSayisi}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={3} className="ia-expand-cell">
                          <div className="ia-panel">
                            <ArizaZamanCubuk
                              seri={zamanCache[`musteri:${m.musteriId}`]}
                              loading={zamanLoading === `musteri:${m.musteriId}`}
                            />
                            {loadingKayit && !paket ? (
                              <div className="ia-empty-inline"><i className="fas fa-spinner fa-spin"></i> Yükleniyor</div>
                            ) : paket?.kayitlar?.length > 0 ? (
                              <>
                                <div className="ia-kayit-list">
                                  {paket.kayitlar.map((k) => (
                                    <article key={k.id} className="ia-kayit-card">
                                      <header className="ia-kayit-head">
                                        <Link
                                          className="ia-kayit-link"
                                          to={`/service-records/${k.id}/update`}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {k.numara}
                                        </Link>
                                        <span className="ia-kayit-meta">{k.durum}</span>
                                        <span className="ia-kayit-meta">{formatApiDate(k.olusturmaTarihi)}</span>
                                      </header>
                                      {k.arizalar?.length > 0 && (
                                        <div className="ia-chips">
                                          {k.arizalar.map((ad) => (
                                            <span key={ad} className="ia-chip">{ad}</span>
                                          ))}
                                        </div>
                                      )}
                                      {k.arizaNotu ? <p className="ia-kayit-note">{k.arizaNotu}</p> : null}
                                      <div className="ia-block">
                                        <h4>Randevular</h4>
                                        {k.randevular?.length > 0 ? (
                                          <ul className="ia-mini-list">
                                            {k.randevular.map((r) => (
                                              <li key={r.id}>
                                                {r.numara} · {formatApiDate(r.tarih).split(' ')[0]} · {r.saat} · {r.durum}
                                              </li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <div className="ia-empty-inline">Randevu yok</div>
                                        )}
                                      </div>
                                      <div className="ia-block">
                                        <h4>Cihazlar</h4>
                                        {k.cihazlar?.length > 0 ? (
                                          <ul className="ia-mini-list">
                                            {k.cihazlar.map((c) => (
                                              <li key={c.id}>{c.urunAdi} · {c.seriNumarasi}</li>
                                            ))}
                                          </ul>
                                        ) : (
                                          <div className="ia-empty-inline">Cihaz yok</div>
                                        )}
                                      </div>
                                    </article>
                                  ))}
                                </div>
                                <TableListFooter
                                  totalRecords={paket.total}
                                  currentPage={sayfa}
                                  totalPages={paket.totalPages || 1}
                                  onPageChange={(p) => changeKayitPage(m.musteriId, p)}
                                  loading={loadingKayit}
                                  embedded
                                />
                              </>
                            ) : (
                              <div className="ia-empty-inline">Servis kaydı bulunamadı</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <TableListFooter
          totalRecords={musteriMeta.total}
          currentPage={musteriPage}
          totalPages={musteriMeta.totalPages || 1}
          onPageChange={setMusteriPage}
          loading={musteriLoading}
          embedded
        />
      </>
    );
  };

  const toggleAriza = (row) => {
    if (!row.adet) return;
    const next = expandedAriza === row.arizaId ? null : row.arizaId;
    setExpandedAriza(next);
    if (!next) return;
    loadZaman(`ariza:${row.arizaId}`, () => fetchArizaZaman(row.arizaId, donem));
    if (arizaUrunCache[row.arizaId]) return;
    setArizaUrunLoading(row.arizaId);
    fetchArizaUrunleri(row.arizaId, donem)
      .then((response) => {
        const liste = response?.basarili ? (response.veri?.urunler || []) : [];
        setArizaUrunCache((prev) => ({ ...prev, [row.arizaId]: liste }));
      })
      .catch(() => {
        setArizaUrunCache((prev) => ({ ...prev, [row.arizaId]: [] }));
      })
      .finally(() => setArizaUrunLoading(null));
  };

  const renderAriza = () => {
    if (arizaLoadingList && arizalar.length === 0) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Liste yükleniyor</div>
        </div>
      );
    }
    if (arizaError) return <div className="error-state">{arizaError}</div>;
    if (arizalar.length === 0) return <EmptyState icon="fa-wrench" text="Arıza bulunamadı" />;

    return (
      <>
        <div className="table-container">
          <table className="admins-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Arıza</th>
                <th>Adet</th>
              </tr>
            </thead>
            <tbody>
              {arizalar.map((a) => {
                const disabled = !a.adet;
                const isExpanded = expandedAriza === a.arizaId;
                const urunler = arizaUrunCache[a.arizaId];
                const loadingUrun = arizaUrunLoading === a.arizaId;
                return (
                  <React.Fragment key={a.arizaId}>
                    <tr
                      className={`${isExpanded ? 'expanded-row' : ''} ${disabled ? 'ia-row--disabled' : ''}`}
                      onClick={() => toggleAriza(a)}
                      style={{ cursor: disabled ? 'default' : 'pointer' }}
                    >
                      <td>
                        {!disabled && (
                          <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: 'var(--text-gray)' }}></i>
                        )}
                      </td>
                      <td style={{ fontWeight: 500 }}>{a.arizaAdi || '—'}</td>
                      <td>{a.adet}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={3} className="ia-expand-cell">
                          <div className="ia-panel">
                            <ArizaZamanCubuk
                              seri={zamanCache[`ariza:${a.arizaId}`]}
                              loading={zamanLoading === `ariza:${a.arizaId}`}
                            />
                            {loadingUrun ? (
                              <div className="ia-empty-inline"><i className="fas fa-spinner fa-spin"></i> Yükleniyor</div>
                            ) : Array.isArray(urunler) && urunler.length > 0 ? (
                              <div className="ia-fault-list">
                                {urunler.map((u) => (
                                  <button
                                    key={u.urunId}
                                    type="button"
                                    className="ia-fault-btn"
                                    onClick={() => openModal(
                                      { urunId: u.urunId, urunAdi: u.urunAdi },
                                      { arizaId: a.arizaId, arizaAdi: a.arizaAdi },
                                    )}
                                  >
                                    <span>{u.urunAdi}</span>
                                    <span className="ia-count">{u.adet}</span>
                                  </button>
                                ))}
                              </div>
                            ) : (
                              <div className="ia-empty-inline">Ürün bulunamadı</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <TableListFooter
          totalRecords={arizaMeta.total}
          currentPage={arizaPage}
          totalPages={arizaMeta.totalPages || 1}
          onPageChange={setArizaPage}
          loading={arizaLoadingList}
          embedded
        />
      </>
    );
  };

  return (
    <main className="main-content istatistik-ariza-page">
      <div className="content-header">
        <h1>Arıza İstatistiği</h1>
      </div>

      <div className="status-tabs" role="tablist" aria-label="Arıza istatistiği">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            className={`status-tab-btn ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <DonemSecici value={donem} onChange={setDonem} />

      {activeTab === 'urun' ? (
        <SearchBox
          value={urunSearch}
          onChange={setUrunSearch}
          placeholder="Ürün adı veya kodu (en az 3 karakter)"
          loading={urunLoading}
          label="Ürün ara"
        />
      ) : activeTab === 'musteri' ? (
        <SearchBox
          value={musteriSearch}
          onChange={setMusteriSearch}
          placeholder="Müşteri adı (en az 3 karakter)"
          loading={musteriLoading}
          label="Müşteri ara"
        />
      ) : (
        <SearchBox
          value={arizaSearch}
          onChange={setArizaSearch}
          placeholder="Arıza adı (en az 3 karakter)"
          loading={arizaLoadingList}
          label="Arıza ara"
        />
      )}

      <ArizaHalka veri={ozet} loading={ozetLoading} />

      <div className="card">
        {activeTab === 'urun' ? renderUrun() : activeTab === 'musteri' ? renderMusteri() : renderAriza()}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)} role="presentation">
          <div
            className="modal-content ia-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ia-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 id="ia-modal-title">{modal.arizaAdi}</h2>
              <button type="button" className="modal-close-btn" onClick={() => setModal(null)} aria-label="Kapat">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <p className="ia-kayit-meta" style={{ marginTop: 0 }}>{modal.urunAdi}</p>
              {modalLoading && modalRows.length === 0 ? (
                <div className="ia-empty-inline"><i className="fas fa-spinner fa-spin"></i> Yükleniyor</div>
              ) : modalError ? (
                <div className="error-state">{modalError}</div>
              ) : modalRows.length === 0 ? (
                <div className="ia-empty-inline">Servis kaydı bulunamadı</div>
              ) : (
                <div className="table-container">
                  <table className="admins-table ia-modal-table">
                    <thead>
                      <tr>
                        <th>Servis No</th>
                        <th>Müşteri</th>
                        <th>Durum</th>
                        <th>Tarih</th>
                        <th>Seri No</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalRows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <Link className="ia-kayit-link" to={`/service-records/${row.id}/update`}>
                              {row.numara}
                            </Link>
                          </td>
                          <td>{row.musteriAdi}</td>
                          <td>{row.durum}</td>
                          <td>{formatApiDate(row.olusturmaTarihi)}</td>
                          <td>{row.seriNumaralari?.length ? row.seriNumaralari.join(', ') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <TableListFooter
                totalRecords={modalMeta.total}
                currentPage={modalPage}
                totalPages={modalMeta.totalPages || 1}
                onPageChange={setModalPage}
                loading={modalLoading}
                embedded
              />
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default IstatistikArizaPage;
